import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, AlertTriangle, MessageCircle } from "lucide-react";
import { supabase } from "../supabaseClient";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { normalizeLocalType, dashboardRouteFromType } from "../utils/userTypes";
import { useCart } from "../shared/contexts/CartContext.jsx";

/** Mapa de status → conteúdo exibido */
function buildStatusInfo(status, dashboardPath, isBaba) {
  const map = {
    sucesso: {
      icon: CheckCircle2,
      iconClass: "text-emerald-500",
      title: "Pagamento aprovado!",
      message: isBaba
        ? "Sua assinatura foi ativada com sucesso. Seu perfil já está com mais visibilidade."
        : "Seu pagamento foi processado com sucesso. Os créditos já foram adicionados à sua conta.",
      primaryLabel: "Ir para o painel",
      primaryPath: dashboardPath,
      autoRedirect: true,
    },
    falha: {
      icon: XCircle,
      iconClass: "text-destructive",
      title: "Pagamento não concluído",
      message:
        "Houve um problema com a transação. Nenhuma cobrança foi realizada. Você pode tentar novamente com outro método.",
      primaryLabel: "Tentar outro método",
      primaryPath: "/pagamento",
      showSupport: true,
    },
    pendente: {
      icon: Clock,
      iconClass: "text-amber-500",
      title: "Aguardando confirmação",
      message:
        "Seu pagamento está em análise. Assim que confirmado, tudo será atualizado automaticamente.",
      primaryLabel: "Voltar ao painel",
      primaryPath: dashboardPath,
    },
    indefinido: {
      icon: AlertTriangle,
      iconClass: "text-amber-500",
      title: "Status não identificado",
      message:
        "Não conseguimos confirmar o status da sua transação. Se o valor foi debitado, entre em contato com o suporte.",
      primaryLabel: "Voltar ao início",
      primaryPath: dashboardPath,
      showSupport: true,
    },
  };
  return map[status] ?? map.indefinido;
}

const AUTO_REDIRECT_SECONDS = 5;

const PagamentoStatus = () => {
  const { status } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();

  const paymentId = searchParams.get("payment_id");
  const externalReference = searchParams.get("external_reference");

  const rawType = localStorage.getItem("user_type") || "family";
  const userType = normalizeLocalType(rawType) || "family";
  const dashboardPath = dashboardRouteFromType(userType);
  const isBaba = userType === "babysitter";

  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECONDS);

  // Sincroniza pagamento aprovado com o banco via RPC
  useEffect(() => {
    async function confirmarPagamentoNoBanco() {
      if (status === "sucesso" && paymentId && externalReference) {
        try {
          const { error: rpcError } = await supabase.rpc("processar_pagamento", {
            p_venda_id: externalReference,
            p_payment_id: paymentId,
            p_status: "approved",
            p_valor: null,
          });
          if (rpcError) console.error("Erro RPC processar_pagamento:", rpcError.message);
        } catch (err) {
          console.error("Erro inesperado na sincronização de pagamento:", err);
        }
      }
    }
    confirmarPagamentoNoBanco();
  }, [status, paymentId, externalReference]);

  // Limpa o carrinho quando pagamento aprovado
  useEffect(() => {
    if (status === "sucesso") {
      clearCart();
    }
  }, [status, clearCart]);

  // Auto-redirect countdown no sucesso
  useEffect(() => {
    if (status !== "sucesso") return;

    const info = buildStatusInfo(status, dashboardPath, isBaba);
    if (!info.autoRedirect) return;

    if (countdown <= 0) {
      navigate(info.primaryPath, { replace: true });
      return;
    }

    const id = setTimeout(() => setCountdown((c) => c - 1), 1_000);
    return () => clearTimeout(id);
  }, [status, countdown, dashboardPath, isBaba, navigate]);

  const info = buildStatusInfo(status, dashboardPath, isBaba);
  const Icon = info.icon;
  const whatsappNumber = import.meta.env.VITE_WHATSAPP_NUMBER;

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-16">
      <Card className="w-full max-w-md p-8 text-center shadow-md">
        <div className="mb-6 flex justify-center">
          <Icon size={64} className={info.iconClass} strokeWidth={1.5} />
        </div>

        <h1 className="mb-2 text-xl font-bold text-foreground">{info.title}</h1>
        <p className="mb-6 text-sm text-muted-foreground">{info.message}</p>

        {paymentId && (
          <p className="mb-6 text-xs text-muted-foreground">
            Comprovante:{" "}
            <strong className="text-foreground">{paymentId}</strong>
          </p>
        )}

        {/* Countdown no sucesso */}
        {status === "sucesso" && countdown > 0 && (
          <p className="mb-4 text-xs text-muted-foreground">
            Redirecionando em <strong>{countdown}s</strong>…
          </p>
        )}

        <div className="flex flex-col gap-3">
          <Button className="w-full" onClick={() => navigate(info.primaryPath)}>
            {info.primaryLabel}
          </Button>

          {info.showSupport && whatsappNumber && (
            <Button variant="outline" className="w-full gap-2" asChild>
              <a
                href={`https://wa.me/${whatsappNumber}?text=Olá, tive um problema com o pagamento e gostaria de ajuda.`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={16} />
                Falar com suporte
              </a>
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PagamentoStatus;
